/**
 * quips.ts — Phase 47 character personality quip system.
 *
 * tickQuips() is called every frame from the GameCanvas update loop.
 * Monitors player behaviour and queues contextual one-liners via queueLine()
 * when conditions are met and cooldowns have expired.
 *
 * Trigger categories:
 *   idle       — cursor stationary (danger-aware: reacts differently if enemies nearby)
 *   moving     — cursor moving but no typing
 *   nearMiss   — enemy passes within NEAR_MISS_PX of cursor
 *   tierChange — tier escalation
 *   waveClear  — new wave begins
 *   general    — low-probability ambient commentary
 *
 * Design rules:
 *   - Queue depth < 2 before every fire (canFire guard).
 *   - No back-to-back repeat of the same line per trigger (lastFired guard).
 */

import type { GameState, GameTier, QuipTrigger } from './types';
import { queueLine, getQueueLength, isDialogueActive } from './dialogue';
import {
  IDLE_QUIPS,
  IDLE_DANGER_QUIPS,
  MOVEMENT_QUIPS,
  NEAR_MISS_QUIPS,
  TIER_CHANGE_QUIPS,
  WAVE_CLEAR_QUIPS,
  GENERAL_QUIPS,
} from './dialogueScript';

// ---------------------------------------------------------------------------
// Thresholds (all in frames @ 60fps)
// ---------------------------------------------------------------------------

const IDLE_THRESHOLD_FRAMES = 180;    // 3s @ 60fps
const MOVING_THRESHOLD_FRAMES = 240;  // 4s @ 60fps
const NEAR_MISS_PX = 90;              // larger than HIT_RADIUS (60px) so quip fires before damage
const GENERAL_QUIP_CHANCE = 1 / 480;  // ~every 8s at 60fps

/** Frames that must pass after a trigger fires before it can fire again. */
const COOLDOWN_FRAMES: Record<QuipTrigger, number> = {
  idle: 480,
  moving: 600,
  nearMiss: 180,
  tierChange: 0,
  waveClear: 0,
  general: 600,
};

// ---------------------------------------------------------------------------
// No-repeat tracking — prevent same line twice in a row per trigger
// ---------------------------------------------------------------------------

const lastFired: Partial<Record<QuipTrigger, string>> = {};

function randomExcluding(pool: readonly string[], trigger: QuipTrigger): string {
  const prev = lastFired[trigger];
  const eligible = prev && pool.length > 1 ? pool.filter(s => s !== prev) : [...pool];
  const pick = eligible[Math.floor(Math.random() * eligible.length)];
  lastFired[trigger] = pick;
  return pick;
}

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

function getCooldown(state: GameState, trigger: QuipTrigger): number {
  return state.quipCooldowns.get(trigger) ?? 0;
}

function setCooldown(state: GameState, trigger: QuipTrigger): void {
  state.quipCooldowns.set(trigger, COOLDOWN_FRAMES[trigger]);
}

function canFire(trigger: QuipTrigger, state: GameState): boolean {
  return getQueueLength() < 2 && getCooldown(state, trigger) <= 0;
}

function fire(trigger: QuipTrigger, line: string, state: GameState): void {
  queueLine(line);
  setCooldown(state, trigger);
}

// ---------------------------------------------------------------------------
// Exported API
// ---------------------------------------------------------------------------

/**
 * Called every frame from the GameCanvas update loop.
 *
 * @param state        — mutable GameState ref
 * @param currentTier  — tier value from currentTierRef
 * @param frameCount   — monotonically increasing frame counter
 */
export function tickQuips(
  state: GameState,
  currentTier: GameTier,
  frameCount: number
): void {
  // --- Decay all active cooldowns ---
  Array.from(state.quipCooldowns.entries()).forEach(([trigger, remaining]) => {
    if (remaining > 0) {
      state.quipCooldowns.set(trigger, remaining - 1);
    }
  });

  // --- Idle timer ---
  state.idleTimer++;

  // --- Moving-without-typing timer ---
  // Increment when mouse moved recently but NOT typed recently.
  // Reset only when player types — brief mouse pauses don't reset.
  const RECENT_FRAMES = 60; // "recent" = within 1s
  const mouseMovedRecently = frameCount - state.lastMouseMoveFrame <= RECENT_FRAMES;
  const typedRecently = frameCount - state.lastTypedFrame <= RECENT_FRAMES;

  if (mouseMovedRecently && !typedRecently) {
    state.movingTimer++;
  } else if (typedRecently) {
    state.movingTimer = 0;
  }

  // --- Tier change ---
  if (currentTier !== state.lastTierForQuip) {
    state.lastTierForQuip = currentTier as number;
    if (currentTier >= 2 && canFire('tierChange', state)) {
      const pool = TIER_CHANGE_QUIPS[currentTier as 2 | 3 | 4 | 5];
      fire('tierChange', randomExcluding(pool, 'tierChange'), state);
    }
  }

  // --- Near-miss: enemy within NEAR_MISS_PX of cursor (only when idle — must be instant) ---
  if (canFire('nearMiss', state) && !isDialogueActive()) {
    for (const enemy of state.enemies) {
      const dist = Math.hypot(enemy.x - state.mouseX, enemy.y - state.mouseY);
      if (dist < NEAR_MISS_PX) {
        fire('nearMiss', randomExcluding(NEAR_MISS_QUIPS, 'nearMiss'), state);
        break;
      }
    }
  }

  // --- Idle: context-aware — different lines when enemies are present ---
  if (state.idleTimer >= IDLE_THRESHOLD_FRAMES && canFire('idle', state)) {
    const pool = state.enemies.length >= 2 ? IDLE_DANGER_QUIPS : IDLE_QUIPS;
    fire('idle', randomExcluding(pool, 'idle'), state);
    state.idleTimer = 0;
  }

  // --- Moving without typing ---
  if (state.movingTimer >= MOVING_THRESHOLD_FRAMES && canFire('moving', state)) {
    fire('moving', randomExcluding(MOVEMENT_QUIPS, 'moving'), state);
    state.movingTimer = 0;
  }

  // --- General ambient commentary ---
  if (canFire('general', state) && Math.random() < GENERAL_QUIP_CHANCE) {
    fire('general', randomExcluding(GENERAL_QUIPS, 'general'), state);
  }
}

/**
 * Called when a new wave begins (wordsCleared crosses a wave boundary).
 */
export function onWaveClear(state: GameState): void {
  if (state.wave < 2) return;
  if (!canFire('waveClear', state)) return;
  fire('waveClear', randomExcluding(WAVE_CLEAR_QUIPS, 'waveClear'), state);
}
